/**
 * SpryBoard Portal (SpryWorkspace Socket.IO)
 *
 * Replaces the encrypted relay Portal with plaintext SpryWorkspace socket events.
 * Uses the spryworkspace-backend Socket.IO board room events:
 *   join-board, leave-board, board-update, board-pointer, board-idle, board-sync
 *   board-user-joined, board-user-left, board-current-users, board-updated, etc.
 *
 * NOTE: Encryption is removed. The server now sees all data in plaintext.
 */
import { CaptureUpdateAction } from "@excalidraw/excalidraw";
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import { newElementWith } from "@excalidraw/element";
import throttle from "lodash.throttle";

import type { UserIdleState } from "@excalidraw/common";
import type { OrderedExcalidrawElement } from "@excalidraw/element/types";
import type {
  OnUserFollowedPayload,
  SocketId,
} from "@excalidraw/excalidraw/types";

import { WS_EVENTS, FILE_UPLOAD_TIMEOUT, WS_SUBTYPES } from "../app_constants";
import { isSyncableElement } from "../data";

import type {
  SocketUpdateData,
  SocketUpdateDataSource,
  SyncableExcalidrawElement,
} from "../data";
import type { TCollabClass } from "./Collab";
import type { Socket } from "socket.io-client";

class Portal {
  collab: TCollabClass;
  socket: Socket | null = null;
  socketInitialized: boolean = false;
  roomId: string | null = null;
  roomKey: string | null = null;
  broadcastedElementVersions: Map<string, number> = new Map();

  constructor(collab: TCollabClass) {
    this.collab = collab;
  }

  open(socket: Socket, id: string, key: string) {
    this.socket = socket;
    this.roomId = id;
    this.roomKey = key;

    // Join SpryWorkspace board room
    this.socket.on("connect", () => {
      if (this.socket && this.roomId) {
        this.socket.emit("join-board", {
          boardId: this.roomId,
          userName: this.collab.state.username,
        });
        trackEvent("share", "room joined");
      }
    });

    // If already connected, join immediately
    if (this.socket.connected) {
      this.socket.emit("join-board", {
        boardId: this.roomId,
        userName: this.collab.state.username,
      });
      trackEvent("share", "room joined");
    }

    // Current users in the room
    this.socket.on("board-current-users", (users: Array<{
      userId: string;
      userName: string;
      userEmail: string;
      userAvatar?: string;
      color: string;
    }>) => {
      const socketIds = users.map((u) => u.userId as unknown as SocketId);
      this.collab.setCollaborators(socketIds);
      for (const user of users) {
        this.collab.updateCollaborator(user.userId as unknown as SocketId, {
          username: user.userName,
          color: user.color,
          avatarUrl: user.userAvatar,
        });
      }

      // If we're the only user, trigger first-in-room behavior
      if (users.length <= 1) {
        this.socket?.emit("_first-in-room");
      }
    });

    // New user joined
    this.socket.on("board-user-joined", (user: {
      userId: string;
      userName: string;
      userAvatar?: string;
      color: string;
    }) => {
      this.collab.updateCollaborator(user.userId as unknown as SocketId, {
        username: user.userName,
        color: user.color,
        avatarUrl: user.userAvatar,
      });
      // Broadcast full scene to the new joiner
      this.broadcastScene(
        WS_SUBTYPES.INIT,
        this.collab.getSceneElementsIncludingDeleted(),
        /* syncAll */ true,
      );
    });

    // User left
    this.socket.on("board-user-left", (user: { userId: string }) => {
      this.collab.removeCollaborator(user.userId as unknown as SocketId);
    });

    return socket;
  }

  close() {
    if (!this.socket) {
      return;
    }
    this.queueFileUpload.flush();

    // Leave the board room
    if (this.roomId) {
      this.socket.emit("leave-board", { boardId: this.roomId });
    }

    this.socket.close();
    this.socket = null;
    this.roomId = null;
    this.roomKey = null;
    this.socketInitialized = false;
    this.broadcastedElementVersions = new Map();
  }

  isOpen() {
    return !!(
      this.socketInitialized &&
      this.socket &&
      this.roomId &&
      this.roomKey
    );
  }

  /**
   * Broadcast data via SpryWorkspace socket events (plaintext, no encryption).
   */
  async _broadcastSocketData(
    data: SocketUpdateData,
    volatile: boolean = false,
    roomId?: string,
  ) {
    if (this.isOpen()) {
      const boardId = roomId ?? this.roomId;

      switch (data.type) {
        case WS_SUBTYPES.INIT:
        case WS_SUBTYPES.UPDATE:
          // Send elements as plaintext via board-update event
          this.socket?.emit("board-update", {
            boardId,
            elements: data.payload.elements,
          });
          break;

        case WS_SUBTYPES.MOUSE_LOCATION:
          // Send pointer position
          this.socket?.emit("board-pointer", {
            boardId,
            pointer: data.payload.pointer,
          });
          break;

        case WS_SUBTYPES.IDLE_STATUS:
          // Send idle state
          this.socket?.emit("board-idle", {
            boardId,
            idle: data.payload.userState !== "active",
          });
          break;

        case WS_SUBTYPES.USER_VISIBLE_SCENE_BOUNDS:
          // Use generic volatile broadcast for viewport bounds
          this.socket?.emit("board-viewport", {
            boardId,
            sceneBounds: data.payload.sceneBounds,
          });
          break;
      }
    }
  }

  queueFileUpload = throttle(async () => {
    try {
      await this.collab.fileManager.saveFiles({
        elements: this.collab.excalidrawAPI.getSceneElementsIncludingDeleted(),
        files: this.collab.excalidrawAPI.getFiles(),
      });
    } catch (error: any) {
      if (error.name !== "AbortError") {
        this.collab.excalidrawAPI.updateScene({
          appState: {
            errorMessage: error.message,
          },
        });
      }
    }

    let isChanged = false;
    const newElements = this.collab.excalidrawAPI
      .getSceneElementsIncludingDeleted()
      .map((element) => {
        if (this.collab.fileManager.shouldUpdateImageElementStatus(element)) {
          isChanged = true;
          return newElementWith(element, { status: "saved" });
        }
        return element;
      });

    if (isChanged) {
      this.collab.excalidrawAPI.updateScene({
        elements: newElements,
        captureUpdate: CaptureUpdateAction.NEVER,
      });
    }
  }, FILE_UPLOAD_TIMEOUT);

  broadcastScene = async (
    updateType: WS_SUBTYPES.INIT | WS_SUBTYPES.UPDATE,
    elements: readonly OrderedExcalidrawElement[],
    syncAll: boolean,
  ) => {
    if (updateType === WS_SUBTYPES.INIT && !syncAll) {
      throw new Error("syncAll must be true when sending SCENE.INIT");
    }

    const syncableElements = elements.reduce((acc, element) => {
      if (
        (syncAll ||
          !this.broadcastedElementVersions.has(element.id) ||
          element.version > this.broadcastedElementVersions.get(element.id)!) &&
        isSyncableElement(element)
      ) {
        acc.push(element);
      }
      return acc;
    }, [] as SyncableExcalidrawElement[]);

    const data: SocketUpdateDataSource[typeof updateType] = {
      type: updateType,
      payload: {
        elements: syncableElements,
      },
    };

    for (const syncableElement of syncableElements) {
      this.broadcastedElementVersions.set(
        syncableElement.id,
        syncableElement.version,
      );
    }

    this.queueFileUpload();

    await this._broadcastSocketData(data as SocketUpdateData);
  };

  broadcastIdleChange = (userState: UserIdleState) => {
    if (this.socket?.id) {
      const data: SocketUpdateDataSource["IDLE_STATUS"] = {
        type: WS_SUBTYPES.IDLE_STATUS,
        payload: {
          socketId: this.socket.id as SocketId,
          userState,
          username: this.collab.state.username,
        },
      };
      return this._broadcastSocketData(
        data as SocketUpdateData,
        true, // volatile
      );
    }
  };

  broadcastMouseLocation = (payload: {
    pointer: SocketUpdateDataSource["MOUSE_LOCATION"]["payload"]["pointer"];
    button: SocketUpdateDataSource["MOUSE_LOCATION"]["payload"]["button"];
  }) => {
    if (this.socket?.id) {
      const data: SocketUpdateDataSource["MOUSE_LOCATION"] = {
        type: WS_SUBTYPES.MOUSE_LOCATION,
        payload: {
          socketId: this.socket.id as SocketId,
          pointer: payload.pointer,
          button: payload.button || "up",
          selectedElementIds:
            this.collab.excalidrawAPI.getAppState().selectedElementIds,
          username: this.collab.state.username,
        },
      };

      return this._broadcastSocketData(
        data as SocketUpdateData,
        true, // volatile
      );
    }
  };

  broadcastVisibleSceneBounds = (
    payload: {
      sceneBounds: SocketUpdateDataSource["USER_VISIBLE_SCENE_BOUNDS"]["payload"]["sceneBounds"];
    },
    roomId: string,
  ) => {
    if (this.socket?.id) {
      const data: SocketUpdateDataSource["USER_VISIBLE_SCENE_BOUNDS"] = {
        type: WS_SUBTYPES.USER_VISIBLE_SCENE_BOUNDS,
        payload: {
          socketId: this.socket.id as SocketId,
          username: this.collab.state.username,
          sceneBounds: payload.sceneBounds,
        },
      };

      return this._broadcastSocketData(
        data as SocketUpdateData,
        true, // volatile
        roomId,
      );
    }
  };

  broadcastUserFollowed = (payload: OnUserFollowedPayload) => {
    if (this.socket?.id) {
      this.socket.emit(WS_EVENTS.USER_FOLLOW_CHANGE, payload);
    }
  };
}

export default Portal;
